/**
 * TanStack Query hooks for the Journal: entries are stored on the server (/api/journal).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { journalApi, type JournalEntryCreate } from '@/services/api';
import { queryKeys } from '@/config/queryClient';

const TITLE_MAX = 80;

/** Entry title for the API (required, 1-255 chars): the first non-empty line of the text. */
export function journalTitle(content: string): string {
  const line = content.split('\n').map((l) => l.trim()).find(Boolean) ?? '';
  return line.length > TITLE_MAX ? `${line.slice(0, TITLE_MAX - 1).trimEnd()}…` : line || '…';
}

export function useJournalEntries(userId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.journal.entries(userId ?? ''),
    queryFn: () => journalApi.getEntries(100, 0),
    enabled: enabled && !!userId,
  });
}

export function useCreateJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: JournalEntryCreate) => journalApi.createEntry(data),
    // Not idempotent: a retry after a lost response would store the entry twice.
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.journal.all }),
  });
}

export function useDeleteJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => journalApi.deleteEntry(entryId),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.journal.all }),
  });
}
